import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import { Loader } from 'lucide-react'
import { LoaderGeneral } from '@/components/common/loaders/LoaderGeneral'
import type { EmployeeUserResponse } from '@/api/model'
import {
  useGetApiCompaniesCurrentEmployeesEmployeeIdActionModules,
  useGetApiCompaniesCurrentEmployeesEmployeeIdServiceModules,
  useGetApiCompaniesCurrentEmployeesEmployeeIdShopModules,
  usePatchApiCompaniesCurrentEmployeesEmployeeIdActionModules,
  usePatchApiCompaniesCurrentEmployeesEmployeeIdServiceModules,
  usePatchApiCompaniesCurrentEmployeesEmployeeIdShopModules,
  useGetApiCompaniesCurrentActionmodules,
  useGetApiCompaniesCurrentServicemodules,
  useGetApiCompaniesCurrentShopmodules,
  getGetApiCompaniesCurrentEmployeesEmployeeIdActionModulesQueryKey,
  getGetApiCompaniesCurrentEmployeesEmployeeIdServiceModulesQueryKey,
  getGetApiCompaniesCurrentEmployeesEmployeeIdShopModulesQueryKey,
  getGetApiCompaniesCurrentEmployeesEmployeeIdQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import { useQueryClient } from '@tanstack/react-query'
import { notify } from '@/lib/notify'

interface EmployeeModulesDialogProps {
  employee: EmployeeUserResponse | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function areArraysEqual(a: number[], b: number[]) {
  if (a.length !== b.length) return false
  const sa = [...a].sort((x, y) => x - y)
  const sb = [...b].sort((x, y) => x - y)
  return sa.every((v, i) => v === sb[i])
}

export function EmployeeModulesDialog({ employee, open, onOpenChange }: EmployeeModulesDialogProps) {
  const { t } = useTranslation(['employees', 'common'])
  const queryClient = useQueryClient()
  const employeeId = employee?.id ?? ''

  const { data: allActionModules, isLoading: loadingAllAction } = useGetApiCompaniesCurrentActionmodules()
  const { data: allServiceModules, isLoading: loadingAllService } = useGetApiCompaniesCurrentServicemodules()
  const { data: allShopModules, isLoading: loadingAllShop } = useGetApiCompaniesCurrentShopmodules()

  const { data: assignedActionIds, isLoading: loadingAction } = useGetApiCompaniesCurrentEmployeesEmployeeIdActionModules(employeeId, { query: { enabled: open && !!employeeId } })
  const { data: assignedServiceIds, isLoading: loadingService } = useGetApiCompaniesCurrentEmployeesEmployeeIdServiceModules(employeeId, { query: { enabled: open && !!employeeId } })
  const { data: assignedShopIds, isLoading: loadingShop } = useGetApiCompaniesCurrentEmployeesEmployeeIdShopModules(employeeId, { query: { enabled: open && !!employeeId } })

  const { mutateAsync: patchAction, isPending: savingAction } = usePatchApiCompaniesCurrentEmployeesEmployeeIdActionModules()
  const { mutateAsync: patchService, isPending: savingService } = usePatchApiCompaniesCurrentEmployeesEmployeeIdServiceModules()
  const { mutateAsync: patchShop, isPending: savingShop } = usePatchApiCompaniesCurrentEmployeesEmployeeIdShopModules()

  const [checkedAction, setCheckedAction] = useState<number[]>([])
  const [checkedService, setCheckedService] = useState<number[]>([])
  const [checkedShop, setCheckedShop] = useState<number[]>([])
  const [originalAction, setOriginalAction] = useState<number[]>([])
  const [originalService, setOriginalService] = useState<number[]>([])
  const [originalShop, setOriginalShop] = useState<number[]>([])

  useEffect(() => {
    if (assignedActionIds?.data) {
      setCheckedAction(assignedActionIds.data)
      setOriginalAction(assignedActionIds.data)
    }
  }, [assignedActionIds])

  useEffect(() => {
    if (assignedServiceIds?.data) {
      setCheckedService(assignedServiceIds.data)
      setOriginalService(assignedServiceIds.data)
    }
  }, [assignedServiceIds])

  useEffect(() => {
    if (assignedShopIds?.data) {
      setCheckedShop(assignedShopIds.data)
      setOriginalShop(assignedShopIds.data)
    }
  }, [assignedShopIds])

  useEffect(() => {
    if (!open) {
      setCheckedAction(originalAction)
      setCheckedService(originalService)
      setCheckedShop(originalShop)
    }
  }, [open])

  const hasChanges =
    !areArraysEqual(checkedAction, originalAction) ||
    !areArraysEqual(checkedService, originalService) ||
    !areArraysEqual(checkedShop, originalShop)

  const isSaving = savingAction || savingService || savingShop
  const isLoading = loadingAllAction || loadingAllService || loadingAllShop || loadingAction || loadingService || loadingShop

  const toggle = (
    id: number,
    setCurrent: React.Dispatch<React.SetStateAction<number[]>>
  ) => {
    setCurrent(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  }

  const handleSave = async () => {
    if (!employeeId) return
    try {
      await Promise.all([
        patchAction({ employeeId, data: checkedAction }),
        patchService({ employeeId, data: checkedService }),
        patchShop({ employeeId, data: checkedShop }),
      ])
      setOriginalAction(checkedAction)
      setOriginalService(checkedService)
      setOriginalShop(checkedShop)
      // Invalidate the cached assignments so reopening the dialog (global staleTime
      // is 5 min) refetches the saved state instead of serving stale IDs. Also refresh
      // the employee detail response, which renders the same assignments.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentEmployeesEmployeeIdActionModulesQueryKey(employeeId) }),
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentEmployeesEmployeeIdServiceModulesQueryKey(employeeId) }),
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentEmployeesEmployeeIdShopModulesQueryKey(employeeId) }),
        queryClient.invalidateQueries({ queryKey: getGetApiCompaniesCurrentEmployeesEmployeeIdQueryKey(employeeId) }),
      ])
      notify.success(t('toasts.modulesSaved'))
      onOpenChange(false)
    } catch (error) {
      notify.apiError(error, t('toasts.modulesSaveError'))
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-350! max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {t('modulesDialog.title', { name: `${employee?.firstName ?? ''} ${employee?.lastName ?? ''}`.trim() })}
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <LoaderGeneral isLoading loadingMessage={t('modulesDialog.loading')} />
        ) : (
          <div className="flex gap-4 flex-1 overflow-hidden">
            <ModuleColumn
              title={t('modulesDialog.actionModules')}
              items={(allActionModules?.data ?? []).map(m => ({ id: m.id!, label: m.name ?? `#${m.id}` }))}
              checked={checkedAction}
              onToggle={id => toggle(id, setCheckedAction)}
            />
            <ModuleColumn
              title={t('modulesDialog.serviceModules')}
              items={(allServiceModules?.data ?? []).map(m => ({ id: m.id!, label: m.name ?? `#${m.id}` }))}
              checked={checkedService}
              onToggle={id => toggle(id, setCheckedService)}
            />
            <ModuleColumn
              title={t('modulesDialog.shopModules')}
              items={(allShopModules?.data ?? []).map(m => ({ id: m.id!, label: m.type ?? `#${m.id}` }))}
              checked={checkedShop}
              onToggle={id => toggle(id, setCheckedShop)}
            />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
          <Button onClick={handleSave} disabled={!hasChanges || isSaving}>
            {isSaving && <Loader className="mr-2 h-4 w-4 animate-spin" />}
            {t('buttons.save', { ns: 'common' })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

interface ModuleColumnProps {
  title: string
  items: { id: number; label: string }[]
  checked: number[]
  onToggle: (id: number) => void
}

function ModuleColumn({ title, items, checked, onToggle }: ModuleColumnProps) {
  const { t } = useTranslation('employees')
  return (
    <div className="flex flex-col flex-1 min-w-0 border rounded-lg overflow-hidden scrollbar-custom">
      <div className="px-4 py-3 border-b bg-muted/50 font-medium text-sm">{title}</div>
      <div className="flex-1 overflow-y-auto">
        {items.length === 0 ? (
          <p className="px-4 py-6 text-sm text-muted-foreground text-center">{t('modulesDialog.empty')}</p>
        ) : (
          <ul className="divide-y">
            {items.map(item => (
              <li
                key={item.id}
                className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-muted/50 transition-colors"
                onClick={() => onToggle(item.id)}
              >
                <Checkbox
                  checked={checked.includes(item.id)}
                  onCheckedChange={() => onToggle(item.id)}
                  onClick={e => e.stopPropagation()}
                />
                <span className="text-sm">{item.label}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
